import React from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

import { EditIcon } from '@/shared/assets/icons/edit-icon';
import { DrawerController } from '@/shared/components/ui/drawer-controller/drawer-controller';
import { Form } from '@/shared/components/ui/form/form';
import { TSubmittedData } from '@/shared/components/ui/form/form.types';
import { MenuItem } from '@/shared/components/ui/menu-item/menu-item';
import { useGroupScopedAction } from '@/shared/features/groups/hooks/use-group-scoped-action/use-group-scoped-action';
import { Can } from '@/shared/features/permissions/components/can/can';
import {
  TUpdateRoleInputSchema,
  TUpdateRoleProps,
} from '@/shared/features/rbac/components/update-role/update-role.types';
import { createUpdateRoleInputSchema } from '@/shared/features/rbac/components/update-role/update-role.validation';
import { UpdateRoleForm } from '@/shared/features/rbac/components/update-role-form/update-role-form';
import { useRbacConfig } from '@/shared/features/rbac/context/rbac-config-context';
import { useUpdateUserRoleMutation } from '@/shared/redux/api/ident/ident-api';
import { showToastError } from '@/shared/utils/toast';

export const UpdateRole: React.FC<TUpdateRoleProps> = ({ role, onClose }) => {
  const { t } = useTranslation();
  const { permissionActions } = useRbacConfig();
  const [updateUserRole] = useUpdateUserRoleMutation();
  const groupScope = useGroupScopedAction({
    action: permissionActions.updateUserRole,
  });

  const handleSubmit = async (
    data: TSubmittedData<TUpdateRoleInputSchema>,
    handleClose: () => void,
  ) => {
    try {
      await updateUserRole({
        userRoleId: role.userRoleId,
        userRole: {
          ...data.dirtyData,
          objectVersion: role!.objectVersion! + 1,
        },
      }).unwrap();
      toast.success(t('rbac:update_role_success'));
      handleClose();
      onClose?.();
    } catch {
      showToastError(t('rbac:update_role_error'));
    }
  };

  return (
    <Can action={permissionActions.updateUserRole} ids={role.userRoleId}>
      <DrawerController
        trigger={
          <MenuItem icon={<EditIcon />}>{t('rbac:update_role')}</MenuItem>
        }
        title={t('rbac:update_role')}
        showDivider={false}
        width="500px"
      >
        {(handleClose) => {
          return (
            <Form<TUpdateRoleInputSchema>
              onSubmit={(event) => handleSubmit(event, handleClose)}
              schema={createUpdateRoleInputSchema(groupScope.requiresGroup)}
              defaultValues={{
                ...role,
                groupId: role.groupId ?? '',
              }}
            >
              {() => (
                <UpdateRoleForm
                  groupSelectProps={groupScope.groupSelectProps}
                  handleClose={() => {
                    onClose?.();
                    handleClose();
                  }}
                  isSystemRole={role?.isSystemRole}
                />
              )}
            </Form>
          );
        }}
      </DrawerController>
    </Can>
  );
};
